<template>
  <div
    ref="modal"
    class="hotspot-modal"
    :class="{ [$class('active')]: poi.id === activePoiId }"
    :style="styles"
    :aria-hidden="`${poi.id !== activePoiId}`"
  >
    <div
      ref="contentContainer"
      class="hotspot-modal__content-container"
    >
      <div
        class="hotspot-modal__content"
        :class="{ 'hotspot-modal__product': poi.product && poi.urlContent}"
      >
        <template v-if="poi.product && poi.urlContent">
          <responsive-image
            :image="poi.product.featuredImage"
            :loaded-animation="false"
            ratio="4:5"
            sizes="200px"
          />

          <div class="hotspot-modal__product-details">
            <p
              v-if="getPromoTag(poi.product)"
              class="text-other-button-xs"
              v-text="getPromoTag(poi.product)"
            />

            <h2
              class="
                hotspot-modal__title
                text-body-s-regular
                text-body-m-regular-desktop
              "
              v-text="poi.product.title"
            />

            <product-prices
              class="hotspot-modal__product-prices"
              :compare-at-price="poi.product.compareAtPrice.amount"
              :price="poi.product.price.amount"
              :varies="poi.product.price.varies"
            />

            <btn
              class="hotspot-modal__button"
              :label="$string('accessibility.shop_now')"
              modifiers="block center primary"
              :url="poi.url"
            />
          </div>
        </template>

        <template v-else>
          <h2
            class="hotspot-modal__title text-body-s-bold text-body-m-bold-desktop"
            v-text="poi.content.title"
          />

          <div
            class="hotspot-modal__text"
            v-html="poi.content.text"
          />

          <div class="hotspot-modal__button-container">
            <btn
              class="hotspot-modal__button"
              :label="$string('accessibility.read_more')"
              modifiers="text"
              :url="poi.url"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Hotspot modal (hotspot)
 * -----------------------------------------------------------------------------
 * Modal pop-up for hotspot point of interest.
 * - Doesn't use <transition> or v-if as we need the modal to exist to calculate
 *   its height to correctly position int.
 * - We also want the content to exist for SEO purposes.
 * - Height has to be set dynamically because CSS variable sets max-height
 *   rather than absolute height so can vary.
 *
 * @param {String} activePoiId - Current active point of interest ID.
 * @param {Object} poi - Current point of interest.
 *
 */
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'

import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

import timings from '~/config/timings'

export default {
  name: 'HotspotModal',

  components: {
    Btn,
    ResponsiveImage,
    ProductPrices,
  },

  props: {
    activePoiId: {
      type: String,
      default: '',
    },
    poi: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'update-active-poi-id': () => true,
  },

  data() {
    return {
      height: 0,
      position: 'top-left',
      styles: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      screenWidth: (state) => state.index.screen.width,
    }),
  },

  watch: {

    /**
     * Watch active POI ID to reset scroll on close after animation delay.
     */
    activePoiId() {
      if (this.activePoiId) {
        return
      }

      setTimeout(() => {
        this.$refs.contentContainer.scroll({ left: 0, top: 0 })
      }, timings.normal)
    },

    /**
     * Watch position to update modal styles based on position of POI.
     * @returns {Object}
     */
    position() {
      if (this.isMobile) {
        this.styles = {}
        return
      }

      let left = ''
      let top = ''

      switch (this.position.x) {
        case 'left':
          left = `calc(${this.poi.position.x}% + ${this.poi.offset.x}px)`
          break

        case 'right':
          left = `calc(${this.poi.position.x}% - var(--hotspot-modal-width) + ${this.poi.offset.x}px)`
          break
      }

      switch (this.position.y) {
        case 'bottom':
          top = `calc(${this.poi.position.y}% - ${this.height}px + ${this.poi.offset.y}px)`
          break

        case 'top':
          top = `calc(${this.poi.position.y}% + ${this.poi.offset.y}px)`
          break
      }

      this.styles = {
        left,
        top,
      }
    },

    /**
     * Watch screen width to update position.
     */
    screenWidth() {
      this.getPosition()
    },
  },

  mounted() {
    this.getPosition()
  },

  methods: {

    /**
     * Get modal position based on media size and active point of interest.
     */
    getPosition() {
      const mediaHeight = this.$parent.$refs.media.offsetHeight
      const mediaWidth = this.$parent.$refs.media.offsetWidth

      const poiPixelPositionX = mediaWidth * (this.poi.position.x / 100)
      const poiPixelPositionY = mediaHeight * (this.poi.position.y / 100)

      const remainingSpaceToRight = mediaWidth - poiPixelPositionX
      const remainingSpaceBelow = mediaHeight - poiPixelPositionY

      const modalWidth = getComputedStyle(document.documentElement)
        .getPropertyValue('--hotspot-modal-width')
        .replace('px', '')

      this.height = this.$refs.modal.offsetHeight

      if (remainingSpaceToRight < modalWidth) {
        if (remainingSpaceBelow < this.height) {
          this.position = { x: 'right', y: 'bottom' }
          return
        }

        this.position = { x: 'right', y: 'top' }
        return
      }

      if (remainingSpaceBelow < this.height) {
        this.position = { x: 'left', y: 'bottom' }
        return
      }

      this.position = { x: 'left', y: 'top' }
    },

    /**
     * Get the additional promo-tag for a product.
     * - If the product is a Collectors Exclusive, at that string from
     *   theme-strings
     * - Promo tag data from product metafields
     * @param {Object} product - Product object.
     * @returns {String}
     */
    getPromoTag(product) {
      if (product?.limits?.collectors) {
        return this.$string('product.collectors_exclusive_promo_tag')
      }

      if (product?.limits?.collectorsEarly) {
        return this.$string('product.collectors_early_promo_tag')
      }

      if (product?.promoTag) {
        return product.promoTag
      }

      return ''
    },
  },
}
</script>

<style lang="scss">
@import './hotspot-modal';
</style>
